import { Button, Card, DuplicateIcon, ImportIcon, LazyLoader, Skeleton, Tab, Tooltip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTimetableStore } from '../../../../../../components/TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../../../../../../components/TimetableVersion/graphql/types';
import { TimetableVersionSubjectsMatrix } from '../../../../../../components/TimetableVersion/TimetableVersionDataAvailabilityMatrix/TimetableVersionSubjectsMatrix';
import { TimetableVersionSubjectsTable } from '../../../../../../components/TimetableVersion/TimetableVersionDataTables/TimetableVersionSubjectsTable';
import { initialMatrix } from '../../../../../../components/TimetableVersion/utils/TimetableVersionTableUtils';
import { useLoadBasicData } from '../../../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../../../hooks/useMemorizedCacheTag';
import {
  TimetableRelationshipProperties,
  use_UpdateTimetableVersionsMutation,
} from '../../../../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../../../../utils/colorUtils';
import { createLessonFilterUrl } from '../../../../../../utils/create-lesson-filter-url';
import { showSuccessUpdateToast, showUserErrorToast } from '../../../../../../utils/toast';
import { availabilityStore } from '../../TimetableVersion';
import styles from '../TimetableVersionData.module.scss';

export const PlanTimetableSubjects = observer(() => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const navigate = useNavigate();

  const store = useTimetableStore();
  const timeAvailabilityStore = store.getTimeAvailabilityStore();

  const [tab, setTab] = useState<'availability' | 'condition'>('availability');
  const [selected, setSelected] = useState<TimetableVersionTableType | null>(null);
  const [loading, setLoading] = useState(false);
  const versionsContext = useMemorizedCacheTag('TIMETABLE_VERSIONS');

  const versionSubjectUuids: string[] = useMemo(() => Array.from(store.subjects.keys()), [store.subjects]);

  const { subjectData } = useLoadBasicData({ pause: !versionUuid });

  const [, updateVersion] = use_UpdateTimetableVersionsMutation();

  const subjectTableData: TimetableVersionTableType[] = useMemo(() => {
    return (
      subjectData?.subjects
        .filter((c) => versionSubjectUuids.includes(c.uuid))
        .map((s) => {
          const { html, label } = hexToColorOption(s.timetableConfig?.color ?? '');
          return {
            uuid: s.uuid,
            name: s.name,
            count: 0,
            shortName: s.shortName,
            color: {
              color: html,
              colorLabel: label,
            },
          };
        }) ?? []
    );
  }, [subjectData?.subjects, versionSubjectUuids]);

  useEffect(() => {
    setSelected(subjectTableData[0]);
  }, [subjectTableData]);

  async function handleChange(items: TimetableRelationshipProperties) {
    if (store.readonly) return;
    if (versionUuid && selected) {
      store.removeConflicts();
      setLoading(true);
      await availabilityStore?.setAvailability('subject', selected.uuid, items, versionUuid);
      timeAvailabilityStore?.setResourceAvailability(
        [
          {
            uuid: selected.uuid,
            days: {
              mon: items.mon,
              tue: items.tue,
              wed: items.wed,
              thu: items.thu,
              fri: items.fri,
              sat: items.sat,
              sun: items.sun,
            },
          },
        ],
        'subject',
      );
      store.setConflicts();
      setLoading(false);
    }
  }

  async function handleImport() {
    if (store.readonly) return;
    setLoading(true);
    if (versionUuid) {
      const result = await updateVersion(
        {
          where: {
            uuid: versionUuid,
          },
          update: {
            subjects: [
              { connect: [{ where: { node: { uuid_IN: delta?.map((c) => c.uuid) } }, edge: { ...initialMatrix } }] },
            ],
          },
        },
        versionsContext,
      );
      if (!result || result.error) {
        showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
      } else {
        showSuccessUpdateToast();
      }
    }
    store.addSubjects(delta ?? []);
    setLoading(false);
  }

  const timeGridEntries = store.timeGridEntries ?? [];

  const delta = subjectData?.subjects.filter((c) => !versionSubjectUuids.includes(c.uuid));

  const onRemove = async (uuids: string[]) => {
    if (store.readonly) return;
    setSelected(subjectTableData[0]);
    await updateVersion(
      {
        where: {
          uuid: versionUuid,
        },
        update: {
          subjects: [{ disconnect: [{ where: { node: { uuid_IN: uuids } } }] }],
        },
      },
      versionsContext,
    );
    store.removeSubjects(uuids);
  };

  return (
    <div>
      <div className={'tks__grid'}>
        <div className='tks__row row end-xs'>
          <Button
            hierarchy='tertiary'
            icon={<ImportIcon />}
            onClick={handleImport}
            disabled={!delta?.length || loading || store.readonly}
          >
            {t('timetableVersion.import', { import: t('subject.title', { count: 2 }) })}
          </Button>
        </div>
        <div className={'tks__row'}>
          <div className={`${subjectTableData.length === 0 ? 'col-xs-12' : 'col-xs-6'} ${styles['left']}`}>
            <TimetableVersionSubjectsTable
              onRowSelection={(selected) => {
                setSelected(selected);
              }}
              versionUuid={versionUuid ?? ''}
              versionSubjectUuids={versionSubjectUuids}
              onRemove={onRemove}
              onShowLessonClick={(uuid) => {
                if (versionUuid) {
                  navigate(
                    createLessonFilterUrl({
                      context: 'stundenplan',
                      versionUuid: versionUuid,
                      column: 'subject',
                      filterValue: uuid,
                    }),
                  );
                }
              }}
            />
          </div>
          {subjectTableData.length !== 0 && (
            <div className={'tks__col col-xs-6'}>
              <Suspense fallback={<LazyLoader />}>
                <Card className={styles['version-data']} contentPadding='none' fitContent>
                  <small>{t('timetableVersion.specificInfo', { for: selected?.name })}</small>
                  <div className={styles['mini-tab']}>
                    <Tab
                      className={styles.tabs}
                      onValueChange={(content) => setTab(content as 'availability' | 'condition')}
                      tabs={[
                        {
                          title: t('availability.title', { count: 2 }),
                          value: 'availability',
                          content: (
                            <TimetableVersionSubjectsMatrix
                              timeGridEntries={timeGridEntries}
                              versionUuid={versionUuid}
                              selected={selected}
                              loading={loading}
                              onChange={handleChange}
                            />
                          ),
                        },
                        {
                          title: t('conditions.title', { count: 2 }),
                          value: 'condition',
                          content: (
                            <div className='px-6 py-4'>
                              <Skeleton />
                            </div>
                          ),
                        },
                      ]}
                    />
                    <div className={styles['additional-actions']}>
                      {tab === 'availability' && (
                        <Tooltip content={t('availability.duplicate')}>
                          <Button hierarchy='ghost' icon={<DuplicateIcon />} disabled />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </Card>
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
